import type { Monster, Stats } from './types'

// Équipement en SIDEGRADES: chaque objet échange une stat contre une autre,
// jamais de gain net — des choix de build, pas une échelle de puissance.
// Règle d'airain: l'arène classée régénère les stats de base depuis la chaîne,
// l'équipement n'y compte donc structurellement pas.
// Source: le butin de la Ligue — chaque rival lâche son objet au premier clear.

/** Déclencheur d'un objet ACTIF en combat (au-delà des simples stats). */
export type GearTrigger =
  | 'focus_sash'
  | 'leftovers'
  | 'life_orb'
  | 'choice_band'
  | 'rocky_helmet'
  | 'cure_berry'
  | 'resist_charm'

export interface Gear {
  id: string
  name: string
  emoji: string
  desc: string
  mods: Partial<Stats> // deltas plats, appliqués après niveau
  /** Effet de combat déclenché (objets tactiques) — casual only. */
  trigger?: GearTrigger
}

export const GEAR_CATALOG: Gear[] = [
  { id: 'badge-stagiaire', name: 'Badge du Stagiaire', emoji: '📛', desc: 'Plein d’énergie, zéro expérience.', mods: { energy: 16, attack: -2 } },
  { id: 'sneakers-testnet', name: 'Sneakers Testnet', emoji: '👟', desc: 'Rapides, mais fragiles.', mods: { speed: 9, defense: -2 } },
  { id: 'parapluie-collateral', name: 'Parapluie Collatéral', emoji: '☂️', desc: 'Couvert, mais encombrant.', mods: { defense: 6, hp: -14 } },
  { id: 'casque-mineur', name: 'Casque de Mineur', emoji: '⛑️', desc: 'Solide comme un bloc.', mods: { defense: 6, attack: -3 } },
  { id: 'grimoire-solidity', name: 'Grimoire Solidity', emoji: '📕', desc: 'Puissant, mais chaque page coûte.', mods: { attack: 6, hp: -26 } },
  { id: 'griffes-apy', name: 'Griffes APY', emoji: '🪝', desc: 'Le rendement a un prix.', mods: { attack: 5, defense: -7 } },
  { id: 'aileron-sequencer', name: 'Aileron Sequencer', emoji: '🛸', desc: 'Toujours premier du bloc.', mods: { speed: 9, hp: -6 } },
  { id: 'meme-maudit', name: 'Mème Maudit', emoji: '🃏', desc: 'Tout en attaque, tant pis pour la garde.', mods: { attack: 7, defense: -10 } },
  { id: 'gilet-reserve', name: 'Gilet Réserve', emoji: '🦺', desc: 'Adossé 1:1 à tes HP.', mods: { hp: 25, attack: -4 } },
  { id: 'pioche-halving', name: 'Pioche Halving', emoji: '⛏️', desc: 'Frappe fort, récompense divisée.', mods: { attack: 4, energy: -30 } },
  { id: 'sablier-liquidation', name: 'Sablier Liquidation', emoji: '⏳', desc: 'L’énergie avant la santé.', mods: { energy: 18, hp: -6 } },
  { id: 'oracle-poche', name: 'Oracle de Poche', emoji: '🔮', desc: 'Voit venir, encaisse moins.', mods: { speed: 6, energy: 8, defense: -3 } },
  { id: 'paws-diamant', name: 'Paws de Diamant', emoji: '💎', desc: 'On ne lâche rien. Lentement.', mods: { hp: 11, speed: -8 } },
  { id: 'turbo-rollup', name: 'Turbo Rollup', emoji: '🌀', desc: 'La vitesse compresse la force.', mods: { speed: 13, attack: -1 } },
  { id: 'coeur-genesis', name: 'Cœur Genesis', emoji: '🫀', desc: 'Le premier bloc bat encore.', mods: { hp: 6, attack: 2, speed: -14 } },
]

// Objets À DÉCLENCHEMENT — tactiques, disponibles librement (casual only, jamais
// comptés en arène classée). Ajoutés APRÈS le catalogue Ligue pour ne pas
// décaler le mapping des butins de rivaux (index 0-14).
export const TRIGGER_GEAR: Gear[] = [
  { id: 'focus-sash', name: 'Focus Sash', emoji: '🎗️', desc: 'À pleine vie, survit à un coup fatal avec 1 HP (1×).', mods: {}, trigger: 'focus_sash' },
  { id: 'leftovers', name: 'Restes', emoji: '🍖', desc: 'Régénère ~6% des HP max à chaque tour.', mods: {}, trigger: 'leftovers' },
  { id: 'life-orb', name: 'Orbe Vie', emoji: '🔮', desc: 'Dégâts +30%, mais perd 10% des HP à chaque coup porté.', mods: {}, trigger: 'life_orb' },
  { id: 'choice-band', name: 'Bandeau Choix', emoji: '🎯', desc: 'ATK +50%, mais verrouillé sur le premier coup utilisé.', mods: {}, trigger: 'choice_band' },
  { id: 'rocky-helmet', name: 'Casque Épineux', emoji: '🪖', desc: 'Blesse l’attaquant de ~17% de ses HP à chaque coup encaissé.', mods: {}, trigger: 'rocky_helmet' },
  { id: 'cure-berry', name: 'Baie Soin', emoji: '🍓', desc: 'Guérit automatiquement le premier effet de statut subi (1×).', mods: {}, trigger: 'cure_berry' },
  { id: 'resist-charm', name: 'Amortisseur', emoji: '🧿', desc: 'Réduit de moitié le premier coup super efficace subi (1×).', mods: {}, trigger: 'resist_charm' },
]

const ALL_GEAR = [...GEAR_CATALOG, ...TRIGGER_GEAR]
export const GEAR_BY_ID = Object.fromEntries(ALL_GEAR.map((g) => [g.id, g])) as Record<string, Gear>
const TRIGGER_IDS = new Set(TRIGGER_GEAR.map((g) => g.id))

const KEY_GEAR = 'cryptoket.gear'

interface GearState {
  owned: string[]
  equipped: Record<string, string> // monsterId → gearId
}

function loadState(): GearState {
  try {
    const s = JSON.parse(localStorage.getItem(KEY_GEAR) ?? '{}') as Partial<GearState>
    return { owned: s.owned ?? [], equipped: s.equipped ?? {} }
  } catch {
    return { owned: [], equipped: {} }
  }
}

function saveState(s: GearState): void {
  localStorage.setItem(KEY_GEAR, JSON.stringify(s))
}

export function ownedGear(): Gear[] {
  // Les objets à déclenchement sont toujours disponibles (tactique, pas puissance).
  const owned = loadState().owned.map((id) => GEAR_BY_ID[id]).filter(Boolean)
  return [...owned, ...TRIGGER_GEAR]
}

export function gearTrigger(gearId: string | undefined): GearTrigger | undefined {
  return gearId ? GEAR_BY_ID[gearId]?.trigger : undefined
}

export function gainGear(gearId: string): boolean {
  const s = loadState()
  if (s.owned.includes(gearId)) return false
  s.owned.push(gearId)
  saveState(s)
  return true
}

export function equippedGearId(monsterId: string): string | null {
  return loadState().equipped[monsterId] ?? null
}

export function equipGear(monsterId: string, gearId: string | null): void {
  const s = loadState()
  if (gearId === null) delete s.equipped[monsterId]
  else if (s.owned.includes(gearId) || TRIGGER_IDS.has(gearId)) s.equipped[monsterId] = gearId
  saveState(s)
}

/** Applique l'objet équipé (deltas plats, planchers de sécurité). */
export function applyGear(monster: Monster, gearOverride?: string | null): Monster {
  const gearId = gearOverride !== undefined ? gearOverride : equippedGearId(monster.id)
  if (!gearId) return monster
  const gear = GEAR_BY_ID[gearId]
  if (!gear) return monster
  const s = monster.stats
  return {
    ...monster,
    gear: gear.id,
    stats: {
      hp: Math.max(50, s.hp + (gear.mods.hp ?? 0)),
      attack: Math.max(5, s.attack + (gear.mods.attack ?? 0)),
      defense: Math.max(5, s.defense + (gear.mods.defense ?? 0)),
      speed: Math.max(5, s.speed + (gear.mods.speed ?? 0)),
      energy: Math.max(0, s.energy + (gear.mods.energy ?? 0)),
    },
  }
}
